import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { Icons } from '../../../shared/helpers/icons';
import { LucideAngularModule } from 'lucide-angular';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { AssessTeamComponent } from '../../components/assess-team/assess-team.component';
import { ReviewRequestComponent } from '../../components/review-request/review-request.component';
import { UserDataService } from '../../../shared/services/auth/userData.service';
import { AssessCaseService } from '../../../shared/services/assessCase.service';
import { IAssessCase } from '../../../shared/models/assessCase';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { EStage, EStatus } from '../../../shared/models/types.enum';
import { FormUtils } from '../../../shared/helpers/formUtils';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { ReviewCancelComponent } from '../../components/review-cancel/review-cancel.component';
import { FormsModule } from '@angular/forms';
import { NgxPaginationModule } from 'ngx-pagination';
import { IThreatAssessmentTeam, IUser } from '../../../shared/models/contact';
import { ContactsService } from '../../../shared/services/contact.service';
import { UploadCaseComponent } from '../../components/upload-case/upload-case.component';
declare var initFlowbite: any;

@Component({
  selector: 'evaluation-dashboard',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    LucideAngularModule,
    AssessTeamComponent,
    ReviewRequestComponent,
    SpinnerComponent,
    ReviewCancelComponent,
    PopupMessageComponent,
    NgxPaginationModule,
    UploadCaseComponent
  ],
  templateUrl: './evaluation-dashboard.component.html',
  styleUrl: './evaluation-dashboard.component.css'
})
export class EvaluationDashboardComponent implements OnInit {

  icons = Icons;
  stageEnum = EStage;
  statusEnum = EStatus;
  currentBuildingId: string | null = null;
  currentUser: IThreatAssessmentTeam | null = null;
  searchQuery: string = '';
  evaluationCases: IAssessCase[] = [];
  filteredEvaluationCases: IAssessCase[] = [];
  selectedCase: IAssessCase | null = null;
  uploadCaseId: string | null = null; // uploaded case whose files and full team answer are being saved
  messageModal: string | null = null;
  popupMessage: string | null = null;
  page = 1;
  itemsPerPage = 10;

  sortOrder: boolean = true;
  showTeam: boolean = false;
  showReviewRequest: boolean = false;
  showReviewModal: boolean = false;
  showCancelReview: boolean = false;
  loadingCases: boolean = true; // true until the cases arrive, so the first render shows the spinner instead of the empty state
  loadingUser: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    private userDataService: UserDataService,
    private assessCaseService: AssessCaseService,
    private contactsService: ContactsService,
    private formUtils: FormUtils
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.loadEvaluationCases();
    this.loadUserInfo();
  }

  loadEvaluationCases(): void {
    // get all case and filter by current stage at first loading
    if (this.currentBuildingId) {
      this.loadingCases = true;
      this.assessCaseService.getBuildingAssessCases(this.currentBuildingId).subscribe({
        next: (response) => {
          this.loadingCases = false;
          if (!response.message) {
            this.evaluationCases = response.filter((item: IAssessCase) => item.current_stage === EStage.Evaluations);
            if (isPlatformBrowser(this.platformId)) { // refresh flowbite effects (tooltip) only in browser
              setTimeout(() => {
                initFlowbite();
              }, 100);
            }
            this.filteredEvaluationCases = this.evaluationCases.length > 0 ? this.evaluationCases.reverse() : [];
          }
        },
        error: (error) => {
          this.loadingCases = false;
          console.error('Error fetching building evaluation cases:', error);
          this.popupMessage = error.message
          this.messageModal = 'unsuccess';
        }
      });
    } else {
      this.loadingCases = false;
    }
  }
    
    loadUserInfo(): void {
      this.loadingUser = true;
      this.contactsService.getUserInfo().subscribe({
        next: (user: IUser) => {
          this.loadingUser = false;
          if (user) {
            this.currentUser = user;
            this.userDataService.setCurrentUser(user);
          }
        },
        error: (error: any) => {
          this.loadingUser = false;
          console.error('Error loading contact info:', error.message);
        }
      });
    }

  sortList(key: keyof typeof this.filteredEvaluationCases[0]): void {
    this.sortOrder = !this.sortOrder;
    this.filteredEvaluationCases.sort((a, b) => {
      const valueA = a[key]?.toString().toLowerCase() || '';
      const valueB = b[key]?.toString().toLowerCase() || '';

      if (valueA < valueB) {
        return this.sortOrder ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortOrder ? 1 : -1;
      }
      return 0;
    });
  }

  filterAssessCases(): void {
    this.filteredEvaluationCases = this.evaluationCases.filter((caseItem) => {
      return (
        caseItem.case_id.toLowerCase()
          .includes(this.searchQuery.toLowerCase()) ||
        caseItem.detail_of_situation?.toLowerCase()
          .includes(this.searchQuery.toLowerCase()) ||
        caseItem.created_by_name?.toLowerCase()
          .includes(this.searchQuery.toLowerCase())
      );
    });
  }

  openModal(type: string, clickedCase?: IAssessCase): void {
    if (type === 'team') {
      this.showTeam = true;
    } else if (type === 'review request') {
      this.showReviewRequest = true;
    } else if (type === 'review modal') {
      this.showReviewModal = true;
    } else if (type === 'cancel review') {
      this.showCancelReview = true;
    }
    this.selectedCase = clickedCase ? clickedCase : null;
  }

  closeModal(type: string): void {
    if (type === 'team') {
      this.showTeam = false;
    } else if (type === 'review request') {
      this.showReviewRequest = false;
    } else if (type === 'review modal') {
      this.showReviewModal = false;
    } else if (type === 'cancel review') {
      this.showCancelReview = false;
    }
    this.resetMessageModal();
    this.selectedCase = null;
  }

  handleSave(message: {type: string, message: string}, actionType: string): void {
    if (actionType === 'team') {
      this.closeModal('team');
    } else if (actionType === 'review request') {
      this.closeModal('review request');
    } else if (actionType === 'cancel review') {
      this.closeModal('cancel review');
    }
    if (message.type === 'success') {
      this.loadEvaluationCases();
      this.messageModal = 'success';
      this.popupMessage = message.message;
      
    } else if (message.type === 'failed') {
      this.messageModal = 'unsuccess';
      this.popupMessage = message.message;
    } 
  }

  isStageReviewPending(clickedCase: IAssessCase): boolean {
    if (clickedCase.behavioral_assessment_screen === this.statusEnum.ReviewPending) {
      return true;
    }
    return false;
  }

  isAllRequiredFormsUndone(selectedCase: IAssessCase): boolean {
    if (selectedCase.threat_assessment_team_status === this.statusEnum.FormUndone || 
        selectedCase.behavioral_assessment_screen === this.statusEnum.FormUndone) {
      return true;
    }   
    return false;
  }

  isUserInTeam(selectedCase: IAssessCase): boolean {
    return this.currentUser?.case_ids?.includes(selectedCase.case_id) ?? false;
  }

  // an uploaded case left in Evaluations finishes the same way it started: upload files and the full team answer
  handleOpenUploadCase(caseId: string): void {
    this.uploadCaseId = caseId;
  }

  handleCloseUploadCase(): void {
    this.uploadCaseId = null;
  }

  handleUploadedCaseCompleted(caseId: string): void {
    this.uploadCaseId = null;
    this.loadEvaluationCases();
    this.messageModal = 'success';
    this.popupMessage = `Case ${caseId} has been uploaded and closed.`;
  }

  goToPage(pageName: string, caseId: string): void {
    this.router.navigate(['/evaluations/' + pageName + '/' + caseId]);
  }

  getTooltip(fieldValue: number, context: string) {
    return this.formUtils.getTooltipContent(fieldValue, context);
  }  
  
  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
  
}
